import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { LinkedItemSearchMenu } from 'shared/components/ui/linked-item-search-menu';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form/use-open-create-goal-dialog';
import { useCreateGoalsWithIdsMutation } from 'shared/hooks/queries/use-create-goals-with-ids-mutation';
import { useGoalByIdQuery } from 'shared/hooks/queries/use-goal-by-id-query';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useUserQuery } from 'shared/hooks/queries/use-user-query';
import { Paths } from 'shared/routes';
import { NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LinkOptions, Task } from 'shared/types/task';
import { v4 as uuidv4 } from 'uuid';

export type LinkedItemSubMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
};

export const LinkedItemSubMenu: React.FC<LinkedItemSubMenuProps> = ({
  tasks,
  onClose,
  ...rest
}) => {
  const { submit: updateTasks } = useUpdateTasks();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const { mutateAsync: createGoalsWithIds } = useCreateGoalsWithIdsMutation();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const [goalsToCreate, setGoalsToCreate] = useState<NewGoalWithID[]>([]);

  const lifeAreaDetailMatch = useMatch({
    path: Paths.LifeAreaDetail,
    end: false,
  });

  const goalDetailMatch = useMatch({
    path: Paths.GoalDetail,
    end: false,
  });

  const { data: user } = useUserQuery();
  const { data: goal } = useGoalByIdQuery(
    user?.uid,
    goalDetailMatch?.params.id,
  );

  const lifeAreaId = lifeAreaDetailMatch?.params.id ?? goal?.lifeAreaId;

  // Combine existing goals with goals to be created
  const allGoals = useMemo(
    () => [...goalsToCreate, ...goals],
    [goalsToCreate, goals],
  );

  // Determine initial value: show value only if all tasks share the same goalId or lifeAreaId
  const value = useMemo<ID | null>(() => {
    if (!tasks.length) {
      return null;
    }

    const firstTask = tasks[0];
    const firstValue = firstTask.goalId ?? firstTask.lifeAreaId;

    if (!firstValue) {
      return null;
    }

    // Check if all tasks have the same value
    const allSame = tasks.every(
      (task) => (task.goalId ?? task.lifeAreaId) === firstValue,
    );

    return allSame ? firstValue : null;
  }, [tasks]);

  const openCreateGoalDialog = useOpenCreateGoalDialog();
  const onChange = (link: { id: ID; type: LinkOptions } | null) => {
    // Calculate goal count changes
    const goalCountChanges = new Map<
      ID,
      { taskCount: number; completedTaskCount: number }
    >();

    tasks.forEach((task) => {
      const oldGoalId = task.goalId;
      const newGoalId = link?.type === LinkOptions.Goal ? link.id : null;
      const isCompleted = !!task.completedAt;

      // Decrement old goal if task was linked to a goal
      if (oldGoalId) {
        const current = goalCountChanges.get(oldGoalId) ?? {
          taskCount: 0,
          completedTaskCount: 0,
        };
        goalCountChanges.set(oldGoalId, {
          taskCount: current.taskCount - 1,
          completedTaskCount:
            current.completedTaskCount - (isCompleted ? 1 : 0),
        });
      }

      // Increment new goal if task is being linked to a goal
      if (newGoalId) {
        const current = goalCountChanges.get(newGoalId) ?? {
          taskCount: 0,
          completedTaskCount: 0,
        };
        goalCountChanges.set(newGoalId, {
          taskCount: current.taskCount + 1,
          completedTaskCount:
            current.completedTaskCount + (isCompleted ? 1 : 0),
        });
      }
    });

    // Update task fields
    const taskUpdates = tasks.map((task) => {
      if (!link) {
        return {
          id: task.id,
          goalId: null,
          lifeAreaId: null,
        };
      }

      if (link.type === LinkOptions.Goal) {
        return {
          id: task.id,
          goalId: link.id,
          lifeAreaId: null,
        };
      }

      return {
        id: task.id,
        goalId: null,
        lifeAreaId: link.id,
      };
    });

    updateTasks(taskUpdates);

    // Update goal counts
    goalCountChanges.forEach((changes, goalId) => {
      if (changes.taskCount !== 0 || changes.completedTaskCount !== 0) {
        updateGoalTaskCount({
          goalId,
          taskCount: changes.taskCount,
          completedTaskCount: changes.completedTaskCount,
        });
      }
    });

    onClose();
  };

  const onCreate = async (name: string) => {
    if (!name.trim()) {
      openCreateGoalDialog({
        lifeAreaId,
        skipNavigation: true,
        tasks,
      });
      onClose();
      return;
    }

    // Calculate goal count changes for old goals (tasks being unlinked)
    const goalCountChanges = new Map<
      ID,
      { taskCount: number; completedTaskCount: number }
    >();

    tasks.forEach((task) => {
      const oldGoalId = task.goalId;
      const isCompleted = !!task.completedAt;

      // Decrement old goal if task was linked to a goal
      if (oldGoalId) {
        const current = goalCountChanges.get(oldGoalId) ?? {
          taskCount: 0,
          completedTaskCount: 0,
        };
        goalCountChanges.set(oldGoalId, {
          taskCount: current.taskCount - 1,
          completedTaskCount:
            current.completedTaskCount - (isCompleted ? 1 : 0),
        });
      }
    });

    // Create temporary goal with ID
    const tempGoal: NewGoalWithID = {
      id: uuidv4(),
      name: name.trim(),
      lifeAreaId,
      taskCount: tasks.length,
      completedTaskCount: tasks.filter((t) => t.completedAt).length,
    };

    setGoalsToCreate((prev) => [...prev, tempGoal]);

    // Create the goal in the backend
    const { oldIdToNewIdMap } = await createGoalsWithIds([tempGoal]);
    const newGoalId = oldIdToNewIdMap[tempGoal.id];

    // Update all tasks to link to the new goal
    const updates = tasks.map((task) => ({
      id: task.id,
      goalId: newGoalId,
      lifeAreaId: null,
    }));

    updateTasks(updates);

    // Update old goal counts
    goalCountChanges.forEach((changes, goalId) => {
      if (changes.taskCount !== 0 || changes.completedTaskCount !== 0) {
        updateGoalTaskCount({
          goalId,
          taskCount: changes.taskCount,
          completedTaskCount: changes.completedTaskCount,
        });
      }
    });

    // Remove from goalsToCreate since it's now created
    setGoalsToCreate((prev) => prev.filter((g) => g.id !== tempGoal.id));

    onClose();
  };

  return (
    <LinkedItemSearchMenu
      {...rest}
      goals={allGoals}
      lifeAreas={lifeAreas}
      value={value}
      onChange={onChange}
      onCreate={onCreate}
      onOutside={onClose}
    />
  );
};
